import { HierarcicalObjectReference } from "@novorender/data-js-api";
import pMap from "p-map";
import { useEffect, useRef } from "react";

import { useExplorerGlobals } from "contexts/explorerGlobals";
import { canBrowseHierarchyOffline, type ObjectDataAccess } from "dataAccess";
import { isLeafNode } from "utils/objectPath";
import { startMeasure } from "utils/performanceMeasure";

/**
 * Maximum concurrency for preloading descendant IDs.
 * Kept low to avoid competing with user-initiated requests.
 */
const PRELOAD_CONCURRENCY = 3;

/**
 * Eagerly preloads `descendants` arrays for the top-level nodes in the scene
 * as soon as the database is available. This warms the in-memory cache on
 * `HierarcicalObjectReference.descendants` so that subsequent "Select All"
 * operations resolve instantly without network calls.
 *
 * The hook runs once per scene load and operates in the background with low
 * concurrency to avoid competing with user-initiated requests.
 *
 * Place this hook high in the component tree (e.g., in the Explorer or App
 * component) so it runs regardless of whether the Object Tree panel is open.
 */
export function useEagerDescendantsPreload(): void {
    const { state } = useExplorerGlobals();
    const dataAccess = state?.dataAccess;
    const lastPreloaded = useRef<ObjectDataAccess | null>(null);

    useEffect(() => {
        if (!dataAccess || lastPreloaded.current === dataAccess) {
            return;
        }
        // Skip offline unless the backend can answer the subtree with no network — a
        // downloaded index warms from OPFS. On the object DB the fast path
        // (signed_download_urls + FAS fetch) and its search-endpoint fallback are both
        // network-only, so the preload can only fire a doomed request. The offline
        // determination mirrors useHandleInit (navigator.onLine + dev override).
        const simulateOfflineInDev = import.meta.env.DEV && import.meta.env.REACT_APP_SIMULATE_OFFLINE === "true";
        if ((!navigator.onLine || simulateOfflineInDev) && !canBrowseHierarchyOffline(dataAccess)) {
            return;
        }
        lastPreloaded.current = dataAccess;

        const abortController = new AbortController();
        const signal = abortController.signal;

        void preloadRootDescendants(dataAccess, signal);

        return () => {
            abortController.abort();
        };
    }, [dataAccess]);
}

async function preloadRootDescendants(dataAccess: ObjectDataAccess, signal: AbortSignal): Promise<void> {
    // Standalone measurement: this background warm-up runs at the tail of viewer
    // init and outlives the VIEWER_LOAD span, so it's tracked as its own root
    // trace rather than nested under viewer load.
    const perf = startMeasure("eagerDescendantsPreload");
    try {
        // Fetch the immediate children of the root (depth 1) and collect them.
        // `for await...of` closes the underlying generator (calls .return()) when
        // we break on abort, unlike a manual .next() loop.
        const topLevelNodes: HierarcicalObjectReference[] = [];
        for await (const node of dataAccess.getChildren(undefined, signal)) {
            if (signal.aborted) {
                break;
            }
            topLevelNodes.push(node);
        }

        if (signal.aborted) {
            perf.end({ aborted: true });
            return;
        }

        perf.mark("topLevelLoaded", { topLevelCount: topLevelNodes.length });

        // Filter to non-leaf nodes that don't already have descendants cached
        const foldersToPreload = topLevelNodes.filter((node) => !isLeafNode(node) && node.descendants === undefined);

        if (foldersToPreload.length === 0) {
            perf.end({ topLevelCount: topLevelNodes.length, foldersToPreload: 0 });
            return;
        }

        // Preload descendants with limited concurrency to avoid competing with
        // user-initiated requests. `signal` makes pMap stop scheduling and reject
        // with AbortError on unmount (swallowed by the outer catch).
        await pMap(
            foldersToPreload,
            // Fast-path only: a scene whose descendants files are absent must not
            // trigger the unbounded subtree-search fallback here, or every
            // top-level folder would spawn thousands of paginated requests that
            // starve user-initiated selections. Failures are best-effort — the
            // user pays the normal fetch delay on first use for that node.
            (node) => dataAccess.warmDescendantIds(node, signal),
            { concurrency: PRELOAD_CONCURRENCY, signal },
        );

        perf.end({ topLevelCount: topLevelNodes.length, foldersPreloaded: foldersToPreload.length });
    } catch {
        // Silently ignore — preloading is best-effort. pMap rejects with an
        // AbortError when the signal fires (unmount / backend change); treat that as
        // an aborted run rather than a failure.
        perf.end({ aborted: signal.aborted, error: !signal.aborted });
    }
}
